/**
 * <MatterAttributesDialog> — the matter VIEW + EDIT dialog (N2, plan
 * `ai-docs/lexide-navigation-and-client-manager-miniplan.md` §3 N2 / Journey 3).
 * The matter twin of {@link ClientAttributesDialog}: reached from the CASOS
 * matter group's ⋯ affordance, so managing a matter no longer requires the
 * full-page exit to /clients/:c/matters/:m (the flow-exit the audit ranked
 * worst; MatterDetailLayout retires in N3).
 *
 * RENAME goes through `matter.update` (shipped runtime-side before N2 asked —
 * P2 handback ac2ec7f3f): displayNames-only per entity-identity INV-3, i.e. it
 * edits the LOCALIZED label and never slug/matterId/on-disk path. The write
 * targets the APP's current locale (pt|en|es), and a `pt` rename also mirrors
 * the primary `name` fallback server-side.
 * ⚠ `unchanged: true` is a SUCCESS no-op (same label for that locale) — render
 * it as "nothing to change", never as a failure (the handback's explicit trap).
 *
 * matterType is IMMUTABLE after creation (vocabulary rule) — displayed, never
 * edited here. "+ Novo caso" opens NewCaseDialog pre-filled with THIS matter,
 * relocating MatterDetailLayout's one unique creation affordance.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Briefcase, Loader2, Plus, X } from "lucide-react";
import { MatterTypeBadge } from "@lexiun/org-admin";
import { cn } from "@/lib/utils";
import {
  dialogOverlayCls,
  dialogPanelCls,
  dialogHeaderCls,
  dialogTitleCls,
  dialogCloseBtnCls,
  dialogLabelCls,
  dialogInputCls,
  dialogFooterCls,
  dialogCancelBtnCls,
  dialogPrimaryBtnCls,
} from "@/components/context-pane/dialog-styles";
import { useGatewayStore } from "@/stores/useGatewayStore";
import { useToastStore } from "@/stores/useToastStore";
import { NewCaseDialog } from "@/components/context-pane/NewCaseDialog";
import { resolveDisplayName } from "@/lib/entity-display";
import { AssignmentsTab } from "@/components/assignments/AssignmentsTab";
import { ChatBriefingButton } from "@/components/chat/ChatBriefingButton";
import { useAuthIdentity } from "@/hooks/useAuthIdentity";
import { useLanguageStore } from "@/stores/useLanguageStore";
import type { MatterListItem } from "@/lib/lexserver-client";

export interface MatterAttributesDialogProps {
  clientId: string;
  matter: MatterListItem;
  onClose: () => void;
}

export function MatterAttributesDialog({ clientId, matter, onClose }: MatterAttributesDialogProps) {
  const { t, i18n } = useTranslation();
  const rpc = useGatewayStore((s) => s.rpc);
  const addToast = useToastStore((s) => s.addToast);
  const queryClient = useQueryClient();
  const lang = useLanguageStore((s) => s.lang);
  const { identity } = useAuthIdentity();

  // The rename edits the label for the APP's locale (matter.update's `locale`
  // param, default pt runtime-side) — so prefill with the SAME resolved label
  // the navigator shows for this locale, not the raw pt-primary `name`.
  const displayed = resolveDisplayName(matter, lang) || matter.matterId;
  const [name, setName] = useState(displayed);
  const [baseName, setBaseName] = useState(displayed);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [newCaseOpen, setNewCaseOpen] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);

  const writeLocale: "pt" | "en" | "es" = i18n.language?.startsWith("en")
    ? "en"
    : i18n.language?.startsWith("es")
      ? "es"
      : "pt";

  useEffect(() => {
    nameRef.current?.select();
  }, []);

  const trimmed = name.trim();
  const canSave = !saving && trimmed !== "" && trimmed !== baseName;

  const handleSave = useCallback(async () => {
    if (!rpc || !canSave) return;
    setSaving(true);
    setServerError(null);
    try {
      const res = await rpc.matterUpdate({
        clientId,
        matterId: matter.matterId,
        name: trimmed,
        locale: writeLocale,
      });
      // `unchanged` is a SUCCESS no-op, not a failure (P2 handback).
      setBaseName(trimmed);
      void queryClient.invalidateQueries({ queryKey: ["matters", clientId] });
      void queryClient.invalidateQueries({ queryKey: ["matters"] });
      addToast({
        text: res.unchanged ? t("matterAttributes.savedUnchanged") : t("matterAttributes.saved"),
        kind: "success",
        autoDismissMs: 3000,
      });
    } catch {
      setServerError(t("matterAttributes.errors.generic"));
    } finally {
      setSaving(false);
    }
  }, [rpc, canSave, clientId, matter.matterId, trimmed, writeLocale, queryClient, addToast, t]);

  // Escape closes; backdrop click does NOT (misclicks must not discard edits —
  // the ClientAttributesDialog rule).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (newCaseOpen) {
    return (
      <NewCaseDialog
        onClose={() => {
          setNewCaseOpen(false);
          onClose();
        }}
        initialClientId={clientId}
        initialMatterId={matter.matterId}
        // NewCaseDialog already invalidates the shared query caches and
        // navigates into the created case (N1 short-URL switchToCase); the
        // matter dialog has nothing extra to refresh.
        onCreated={() => {}}
      />
    );
  }

  return (
    <div data-testid="matter-attributes-dialog" className={dialogOverlayCls}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("matterAttributes.title")}
        onClick={(e) => e.stopPropagation()}
        className={dialogPanelCls}
      >
        <div className={dialogHeaderCls}>
          <h2 className={cn(dialogTitleCls, "flex items-center gap-1.5")}>
            <Briefcase size={14} className="text-cream-500" aria-hidden />
            {t("matterAttributes.title")}
          </h2>
          <button type="button" onClick={onClose} aria-label={t("actions.close")} className={dialogCloseBtnCls}>
            <X size={14} />
          </button>
        </div>

        {/* Read-only meta — the TYPE is immutable after creation (vocabulary
            rule); shown, never edited. */}
        <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-caption text-cream-500">
          {matter.matterType && <MatterTypeBadge matterType={matter.matterType} />}
          <span className="font-mono text-[11px]">{matter.matterId}</span>
        </div>

        {/* Localized label — the rename writes THIS locale's displayName. */}
        <label className={dialogLabelCls}>{t("matterAttributes.nameLabel")}</label>
        <input
          ref={nameRef}
          data-testid="matter-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void handleSave();
            }
          }}
          placeholder={t("matterAttributes.namePlaceholder")}
          maxLength={200}
          className={dialogInputCls}
        />
        <p className="mt-1 text-caption text-cream-500">{t("matterAttributes.nameHint")}</p>

        {serverError && <p className="mt-2 text-caption text-red-500">{serverError}</p>}

        {/* ── RESPONSÁVEIS + BRIEFING (N3 relocation) — MatterDetailLayout was
            the SOLE carrier of the matter-scope AssignmentsTab and the
            matter-tier briefing; they move here before the page retires. */}
        <section className="mt-5" data-testid="matter-assignments-section">
          <AssignmentsTab scope="matter" clientId={clientId} matterId={matter.matterId} />
        </section>
        <section className="mt-4" data-testid="matter-briefing-section">
          <ChatBriefingButton
            tier="matter"
            label={displayed}
            clientId={clientId}
            matterId={matter.matterId}
            matterType={matter.matterType}
            userId={identity?.userId}
            orgId={identity?.orgId}
          />
        </section>

        {/* Footer — left: + Novo caso (relocated from MatterDetailLayout);
            right: cancel / save. */}
        <div className={cn(dialogFooterCls, "justify-between")}>
          <button
            type="button"
            data-testid="matter-new-case"
            onClick={() => setNewCaseOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-body-sm text-cream-500 transition-colors hover:bg-cream-100/70 hover:text-coffee-700 dark:text-cream-400 dark:hover:bg-coffee-800/60"
          >
            <Plus size={13} aria-hidden />
            {t("matterAttributes.newCase")}
          </button>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className={dialogCancelBtnCls}>
              {t("actions.cancel")}
            </button>
            <button
              type="button"
              data-testid="matter-attributes-save"
              onClick={() => void handleSave()}
              disabled={!canSave}
              className={dialogPrimaryBtnCls(canSave)}
            >
              {saving && <Loader2 size={13} className="animate-spin" aria-hidden />}
              {t("matterAttributes.save")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
